
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { reportAPI } from '../../services/api';
import 'leaflet/dist/leaflet.css';
import './ReportSchoolMap.css';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';
import Swal from 'sweetalert2';

const ReportSchoolMap = () => {
    const { t } = useTranslation(['reports', 'common']);
    const [schoolData, setSchoolData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [totalSchools, setTotalSchools] = useState(0);
    const [totalStudents, setTotalStudents] = useState(0);
    const [enrollmentYear, setEnrollmentYear] = useState(null);

    // Filter options
    const [ppdOptions, setPpdOptions] = useState([]);
    const [schoolOptions, setSchoolOptions] = useState([]);

    // Filter selections
    const [ppdFilter, setPpdFilter] = useState('');
    const [schoolFilter, setSchoolFilter] = useState('');

    // Report Issue modal states
    const [showReportIssueModal, setShowReportIssueModal] = useState(false);
    const [screenshot, setScreenshot] = useState(null);

    const getRadius = (studentCount) => {
        if (studentCount > 500) return 15;
        if (studentCount > 200) return 10;
        if (studentCount > 100) return 7;
        return 5;
    };

    const getColor = (studentCount) => {
        if (studentCount > 500) return '#dc3545';
        if (studentCount > 200) return '#ffc107';
        if (studentCount > 100) return '#28a745';
        return '#667eea';
    };

    // Load filter options on mount
    useEffect(() => {
        loadFilterOptions();
    }, []);

    // Load map data when component mounts or filters change
    useEffect(() => {
        loadMapData();
    }, [ppdFilter, schoolFilter]);

    // Reload school options when PPD filter changes
    useEffect(() => {
        if (ppdFilter) {
            loadSchoolOptions(ppdFilter);
        } else {
            loadSchoolOptions(null); // Load all schools
        }
        // Reset school filter when PPD changes
        setSchoolFilter('');
    }, [ppdFilter]);

    const loadFilterOptions = async () => {
        try {
            // Load all PPDs for filter dropdown
            const ppds = await reportAPI.getSchoolMapPPDs();
            setPpdOptions(ppds || []);

            // Load all schools initially
            const schools = await reportAPI.getSchoolMapSchools(null);
            setSchoolOptions(schools || []);
        } catch (err) {
            console.error("ReportSchoolMap: Error loading filter options:", err);
        }
    };

    const loadSchoolOptions = async (ppd) => {
        try {
            const schools = await reportAPI.getSchoolMapSchools(ppd);
            setSchoolOptions(schools || []);
        } catch (err) {
            console.error("ReportSchoolMap: Error loading school options:", err);
        }
    };

    const loadMapData = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await reportAPI.getSchoolMapData(ppdFilter, schoolFilter);

            // Handle new response structure with metadata
            if (result.schools) {
                setSchoolData(result.schools);
                setTotalSchools(result.totalSchools);
                setTotalStudents(result.totalStudents);
                setEnrollmentYear(result.enrollmentYear);
            } else {
                // Fallback for old response format (array)
                setSchoolData(result);
                setTotalSchools(result.length);
                setTotalStudents(result.reduce((sum, school) => sum + school.studentCount, 0));
            }

        } catch (err) {
            console.error("ReportSchoolMap: Error loading map data:", err);
            setError('Failed to load map data. You may not have permission to view this report.');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilter = () => {
        loadMapData();
    };

    // Handle Report Issue button click
    const handleReportIssueClick = async () => {
        try {
            Swal.fire({
                title: t('reports:common.messages.capturingScreenshot'),
                html: t('reports:common.messages.pleaseWaitScreenshot'),
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const canvas = await html2canvas(document.body, {
                useCORS: true,
                allowTaint: true,
                scrollY: -window.scrollY,
                scrollX: -window.scrollX,
                width: window.innerWidth,
                height: window.innerHeight
            });

            const imageData = canvas.toDataURL('image/png');
            setScreenshot(imageData);
            setShowReportIssueModal(true);
            Swal.close();
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            Swal.fire({
                icon: 'error',
                title: t('reports:common.messages.captureFailed'),
                text: t('reports:common.messages.captureFailedMessage')
            });
        }
    };

    if (loading) {
        return <div className="loading-container">Loading Map...</div>;
    }

    if (error) {
        return <div className="error-container">{error}</div>;
    }

    return (
        <div className="map-report-container">
            {/* Header with Logo and Bilingual Title */}
            <div className="header-container">
                <img className="logo" src="/KTC.png" alt="KTC" />
                <h2>
                    {t('reports:schoolMap.title')} - {enrollmentYear} ({t('reports:schoolMap.titleEn')} - {enrollmentYear})
                </h2>
                <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('common:reportIssue', { lng: 'en' })}>
                    <i className="fas fa-bug"></i>
                </button>
            </div>

            <div className="report-body">
                <div className="filter-panel">
                    <h5><i className="fas fa-filter"></i> {t('reports:schoolMap.filters.title', {defaultValue: 'Filters'})}</h5>
                    <hr />
                    <div className="form-group">
                        <label>{t('reports:schoolMap.filters.ppd')}:</label>
                        <select
                            className="form-control"
                            value={ppdFilter}
                            onChange={(e) => setPpdFilter(e.target.value)}
                        >
                            <option value="">{t('reports:summary.all', {defaultValue: 'All Districts'})}</option>
                            {ppdOptions.map(ppd => (
                                <option key={ppd} value={ppd}>{ppd}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>{t('reports:summary.filters.school', {defaultValue: 'School'})}:</label>
                        <select
                            className="form-control"
                            value={schoolFilter}
                            onChange={(e) => setSchoolFilter(e.target.value)}
                        >
                            <option value="">{t('reports:summary.all', {defaultValue: 'All Schools'})}</option>
                            {schoolOptions.map(school => (
                                <option key={school.code} value={school.code}>
                                    {school.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="stats-card">
                        <i className="fas fa-school" style={{fontSize: '24px', color: '#667eea'}}></i>
                        <h3>{totalSchools}</h3>
                        <p>{t('reports:schoolMap.stats.totalSchools')}</p>
                    </div>

                    <div className="stats-card">
                        <i className="fas fa-user-graduate" style={{fontSize: '24px', color: '#764ba2'}}></i>
                        <h3>{totalStudents.toLocaleString()}</h3>
                        <p>{t('reports:schoolMap.stats.totalStudents')}</p>
                    </div>

                    {enrollmentYear && (
                        <div className="stats-card">
                            <i className="fas fa-calendar" style={{fontSize: '24px', color: '#28a745'}}></i>
                            <h3>{enrollmentYear}</h3>
                            <p>{t('reports:schoolMap.stats.enrollmentYear')}</p>
                        </div>
                    )}

                    {/* Legend */}
                    <div className="legend">
                        <div className="legend-title"><i className="fas fa-info-circle"></i> {t('reports:schoolMap.legend.title')}</div>
                        <div className="legend-item">
                            <div className="legend-circle" style={{width: '30px', height: '30px', backgroundColor: '#dc3545', opacity: 0.7, borderRadius: '50%'}}></div>
                            <span>{t('reports:schoolMap.legend.veryLarge')} (&gt;500)</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-circle" style={{width: '20px', height: '20px', backgroundColor: '#28a745', opacity: 0.7, borderRadius: '50%'}}></div>
                            <span>{t('reports:schoolMap.legend.medium')} (100-500)</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-circle" style={{width: '10px', height: '10px', backgroundColor: '#667eea', opacity: 0.7, borderRadius: '50%'}}></div>
                            <span>{t('reports:schoolMap.legend.small')} (&lt;100)</span>
                        </div>
                    </div>
                </div>

                <div className="map-container-main">
                    <MapContainer center={[5.9804, 116.0735]} zoom={8} scrollWheelZoom={true} className="map-view">
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {schoolData.map(school => (
                            <CircleMarker
                                key={school.code}
                                center={[school.latitude, school.longitude]}
                                radius={getRadius(school.studentCount)}
                                pathOptions={{
                                    fillColor: getColor(school.studentCount),
                                    color: '#fff',
                                    weight: 2,
                                    opacity: 1,
                                    fillOpacity: 0.7
                                }}
                            >
                                <Popup>
                                    <div className="school-popup">
                                        <h4>{school.name}</h4>
                                        <p><strong>Code:</strong> {school.code}</p>
                                        <p><strong>PPD:</strong> {school.ppd}</p>
                                        <p><strong>Students:</strong> {school.studentCount}</p>
                                        <p><strong>Address:</strong> {school.address || 'N/A'}</p>
                                    </div>
                                </Popup>
                            </CircleMarker>
                        ))}
                    </MapContainer>

                    {/* Remarks Section */}
                    <div className="map-remarks">
                        <div className="remarks-title"><i className="fas fa-info-circle"></i> Nota / Remarks</div>
                        <ul className="remarks-list">
                            <li>Sekolah: Hanya sekolah yang mempunyai koordinat GPS (Latitud/Longitud) dikira dan dipaparkan di peta.</li>
                            <li>Pelajar: Hanya pendaftaran AKTIF untuk tahun semasa ({enrollmentYear}) dikira (Warganegara & Pemastautin Tetap sahaja).</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Report Issue Modal */}
            {showReportIssueModal && (
                <ReportIssueModal
                    show={showReportIssueModal}
                    onClose={() => setShowReportIssueModal(false)}
                    screenshot={screenshot}
                    context={{
                        screen: 'Report School Map',
                        ppdFilter: ppdFilter || 'All Districts',
                        schoolFilter: schoolFilter || 'All Schools',
                        totalSchools: totalSchools,
                        totalStudents: totalStudents
                    }}
                />
            )}
        </div>
    );
};

export default ReportSchoolMap;
